/**
 * The one source of truth for the order officers are listed in — the officer
 * graph's rows, every picker/dropdown, the stats filter, exports.
 *
 * Default order is the natural one (OT, then SOS, then Main — see
 * naturalSortOfficers). When someone drags a row to a new place, the whole list
 * is saved on the SESSION (server side), so everyone working the same session ID
 * sees the same order; it rides along with the roster state, so a change shows up
 * on the other tabs. Every consumer goes through naturalSortOfficers; React
 * consumers subscribe with useOfficerOrder() so they re-render when it changes.
 * Same shape as utils/whatsappFooter.
 */
import { useSyncExternalStore } from 'react';
import { apiClient } from '@/api/client';

let order: string[] = [];
let loaded = false;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() { listeners.forEach(l => l()); }

const sameOrder = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

export function getOfficerOrder(): readonly string[] { return order; }

/** Adopt the order that came back with a roster-state payload. */
export function adoptOfficerOrder(value: readonly string[] | undefined): void {
  if (value === undefined) return;
  loaded = true;
  if (sameOrder(value, order)) return;
  order = [...value];
  emit();
}

/** Forget what we know — call when the session changes. */
export function resetOfficerOrder(): void {
  loaded = false;
  inFlight = null;
  if (order.length) { order = []; emit(); }
}

/** Fetch the session's order once if no state payload has delivered it yet. */
function loadOfficerOrder(): Promise<void> {
  if (loaded || inFlight) return inFlight ?? Promise.resolve();
  inFlight = apiClient
    .getOfficerOrder()
    .then(({ officer_order }) => { adoptOfficerOrder(officer_order); })
    .catch(() => { /* offline — the default order stands */ })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Save a full order to the session (empty restores the default). Shows at once, rolls
 *  back if the write fails (e.g. a view-only session). Resolves true when saved. */
export async function saveOfficerOrder(next: string[]): Promise<boolean> {
  const previous = order;
  order = next;
  loaded = true;
  emit();
  try {
    await apiClient.setOfficerOrder(next);
    return true;
  } catch {
    order = previous;
    emit();
    return false;
  }
}

/**
 * Applies the saved order to ids already in default (natural) order. Officers the
 * saved order has never seen (just added) keep their default place: each one goes
 * right after the nearest default-order neighbour above it that is already placed,
 * so a new SOS lands among the SOS rows instead of at the very bottom.
 */
export function applyOfficerOrder(naturalIds: string[], saved: readonly string[] = order): string[] {
  if (saved.length === 0) return naturalIds;
  const present = new Set(naturalIds);
  const result = saved.filter(id => present.has(id));
  if (result.length === 0) return naturalIds;
  const placed = new Set(result);
  let anchor: string | null = null;   // last placed id seen walking the default order
  for (const id of naturalIds) {
    if (placed.has(id)) { anchor = id; continue; }
    const at = anchor === null ? 0 : result.indexOf(anchor) + 1;
    result.splice(at, 0, id);
    placed.add(id);
    anchor = id;
  }
  return result;
}

/** Re-renders the caller when the order changes; returns the saved order (stable identity until then). */
export function useOfficerOrder(): readonly string[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      void loadOfficerOrder();
      return () => { listeners.delete(cb); };
    },
    getOfficerOrder,
  );
}
